import { FormMethods, notify } from '@wyz/ui';
import {
  CustomersBrandsForm,
  CustomersConfigurationForm,
  CustomersContactForm,
  CustomersCriteriaForm,
  CustomersDeliveryBillingForm,
  CustomersGeneralForm,
  CustomersQuoteBrandsForm,
  CustomersQuoteExpressForm,
  CustomersRedistributionBrandsForm,
  CustomersSettingTemplateForm,
  CustomersSuppliersForm,
  InternalStocksForm,
} from '~/connect/components';

import { IErrorApiResponse, IValidationError } from '@wyz/types';
import { useOnUpdate } from '@wyz/utils';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useI18n } from '~/common/context';
import { Validations } from '~/common/helpers';
import { useCommonErrorTranslation } from '~/common/hooks';
import { RESERVED_CRITERIA_CODE } from '~/common/schemas';
import { CommonErrorCodesEnumType } from '~/common/types';
import { useCustomerFormLevel } from '~/connect/hooks';
import { CustomerFormStepsHelpers } from '~/connect/helpers';
import {
  CustomerCriterion,
  CustomerFormStepValues,
  CustomerStepForms,
  ICustomerForm,
  ICustomersFormsResult,
} from '~/connect/types';

type ICustomersFormArgs = {
  defaultValues?: Partial<ICustomerForm>;
  error?: IErrorApiResponse<
    IValidationError<CommonErrorCodesEnumType>,
    CommonErrorCodesEnumType
  > | null;
};

export const useCustomerForm = ({
  defaultValues,
  error,
}: ICustomersFormArgs = {}): ICustomersFormsResult => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const stepOptions = <TStep extends keyof CustomerFormStepValues>(
    step: TStep,
  ) =>
    CustomerFormStepsHelpers.buildStepFormOptions<
      CustomerFormStepValues[TStep]
    >(step, defaultValues);

  const generalFormMethods = useForm<CustomerFormStepValues['GENERAL_FORM']>(
    stepOptions('GENERAL_FORM'),
  );
  const criteriaFormMethods = useForm<CustomerFormStepValues['CRITERIA_FORM']>(
    stepOptions('CRITERIA_FORM'),
  );
  const quoteExpressFormMethods = useForm<
    CustomerFormStepValues['QUOTE_EXPRESS_FORM']
  >(stepOptions('QUOTE_EXPRESS_FORM'));
  const contactFormMethods = useForm<CustomerFormStepValues['CONTACT_FORM']>(
    stepOptions('CONTACT_FORM'),
  );
  const deliveryAndBillingFormMethods = useForm<
    CustomerFormStepValues['DELIVERY_FORM']
  >(stepOptions('DELIVERY_FORM'));
  const configurationFormMethods = useForm<
    CustomerFormStepValues['CONFIGURATION_FORM']
  >(stepOptions('CONFIGURATION_FORM'));
  const suppliersFormMethods = useForm<
    CustomerFormStepValues['SUPPLIERS_FORM']
  >(stepOptions('SUPPLIERS_FORM'));
  const brandsFormMethods = useForm<CustomerFormStepValues['BRANDS_FORM']>(
    stepOptions('BRANDS_FORM'),
  );
  const quoteBrandsFormMethods = useForm<
    CustomerFormStepValues['QUOTE_BRANDS_FORM']
  >(stepOptions('QUOTE_BRANDS_FORM'));
  const redistributionBrandsFormMethods = useForm<
    CustomerFormStepValues['REDISTRIBUTION_BRANDS_FORM']
  >(stepOptions('REDISTRIBUTION_BRANDS_FORM'));
  const settingTemplateFormMethods = useForm<
    CustomerFormStepValues['SETTING_TEMPLATE_FORM']
  >(stepOptions('SETTING_TEMPLATE_FORM'));
  const internalStocksFormMethods = useForm<
    CustomerFormStepValues['INTERNAL_STOCK_FORM']
  >(stepOptions('INTERNAL_STOCK_FORM'));

  const forms = React.useMemo<CustomerStepForms>(
    () => ({
      GENERAL_FORM: {
        methods: generalFormMethods,
        component: CustomersGeneralForm,
        stepKey: 'GENERAL_FORM',
      },
      CRITERIA_FORM: {
        methods: criteriaFormMethods,
        component: CustomersCriteriaForm,
        stepKey: 'CRITERIA_FORM',
      },
      QUOTE_EXPRESS_FORM: {
        methods: quoteExpressFormMethods,
        component: CustomersQuoteExpressForm,
        stepKey: 'QUOTE_EXPRESS_FORM',
      },
      CONTACT_FORM: {
        methods: contactFormMethods,
        component: CustomersContactForm,
        stepKey: 'CONTACT_FORM',
      },
      DELIVERY_FORM: {
        methods: deliveryAndBillingFormMethods,
        component: CustomersDeliveryBillingForm,
        stepKey: 'DELIVERY_FORM',
      },
      CONFIGURATION_FORM: {
        methods: configurationFormMethods,
        component: CustomersConfigurationForm,
        stepKey: 'CONFIGURATION_FORM',
      },
      SUPPLIERS_FORM: {
        methods: suppliersFormMethods,
        component: CustomersSuppliersForm,
        stepKey: 'SUPPLIERS_FORM',
      },
      BRANDS_FORM: {
        methods: brandsFormMethods,
        component: CustomersBrandsForm,
        stepKey: 'BRANDS_FORM',
      },
      QUOTE_BRANDS_FORM: {
        methods: quoteBrandsFormMethods,
        component: CustomersQuoteBrandsForm,
        stepKey: 'QUOTE_BRANDS_FORM',
      },
      REDISTRIBUTION_BRANDS_FORM: {
        methods: redistributionBrandsFormMethods,
        component: CustomersRedistributionBrandsForm,
        stepKey: 'REDISTRIBUTION_BRANDS_FORM',
      },
      SETTING_TEMPLATE_FORM: {
        methods: settingTemplateFormMethods,
        component: CustomersSettingTemplateForm,
        stepKey: 'SETTING_TEMPLATE_FORM',
      },
      INTERNAL_STOCK_FORM: {
        methods: internalStocksFormMethods,
        component: InternalStocksForm,
        stepKey: 'INTERNAL_STOCK_FORM',
      },
    }),
    [
      generalFormMethods,
      criteriaFormMethods,
      quoteExpressFormMethods,
      contactFormMethods,
      deliveryAndBillingFormMethods,
      configurationFormMethods,
      suppliersFormMethods,
      brandsFormMethods,
      quoteBrandsFormMethods,
      redistributionBrandsFormMethods,
      settingTemplateFormMethods,
      internalStocksFormMethods,
    ],
  );

  useOnUpdate(() => {
    if (error?.errors) {
      Object.values(forms).forEach((stepForm) => {
        Validations.setFormErrors({
          setError: stepForm.methods.setError as FormMethods['setError'],
          errors: Validations.formatValidationErrors(error.errors, t),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.errors]);

  useOnUpdate(() => {
    if (error?.message) {
      notify.error(<Trans i18nKey={resolveErrorKey(error?.message)} />, {
        id: 'toast-error-customer-form-error',
      });
    }
  }, [error?.message, resolveErrorKey]);

  const watchedLevel = useCustomerFormLevel({
    parent: generalFormMethods.getValues('parent'),
  });

  return {
    forms,
    internalCriteria: [
      {
        id: RESERVED_CRITERIA_CODE.COUNTRY,
        valueId: deliveryAndBillingFormMethods.getValues(
          'deliveryInformation.country.isoCode',
        ),
      },
      {
        id: RESERVED_CRITERIA_CODE.CUSTOMER,
        valueId: generalFormMethods.getValues('parent.id.code'),
      },
      {
        id: RESERVED_CRITERIA_CODE.LEVEL,
        valueId: String(watchedLevel),
      },
    ].filter((criterion) =>
      Boolean(criterion.valueId),
    ) as Array<CustomerCriterion>,
  };
};
